import { z } from "zod";

import {
	type GetImpulseFormInput,
	type GetImpulseFormOutput,
	ImpulseFormShape,
	ImpulseFormUnit,
} from "~/tools/impulse-form";
import { isStringDirty } from "~/tools/is-string-dirty";
import { trim } from "~/tools/trim";

import { WidgetColor } from "../color/form-state";

const ABSOLUTE_MODE = "absolute";
const ALL_MODES = [ABSOLUTE_MODE, "percent"] as const;
const modeSchema = z.enum(ALL_MODES);

type WidgetThresholdModeVariant = z.infer<typeof modeSchema>;
type WidgetThresholdsMode = ImpulseFormUnit<string, ReadonlyArray<string>, WidgetThresholdModeVariant>;

const WidgetThresholdsMode = {
	default: ABSOLUTE_MODE,
	all: ALL_MODES,

	init(initial = ABSOLUTE_MODE): WidgetThresholdsMode {
		return ImpulseFormUnit(initial, {
			schema: modeSchema,
		});
	},
} as const;

const DEFAULT_STEP = {
	color: WidgetColor.critical,
	value: "",
} satisfies GetImpulseFormInput<WidgetThresholdsStep>;

const FALLBACK_STEP = {
	color: WidgetColor.critical,
	value: 0,
} as GetImpulseFormOutput<WidgetThresholdsStep>;

type WidgetThresholdsColor = ImpulseFormUnit<string>;

type WidgetThresholdsValue = ImpulseFormUnit<string, ReadonlyArray<string>, number>;

type WidgetThresholdsStep = ImpulseFormShape<{
	color: WidgetThresholdsColor;
	value: WidgetThresholdsValue;
}>;

const WidgetThresholdsStep = {
	default: DEFAULT_STEP,
	fallback: FALLBACK_STEP,

	init(initial?: Partial<GetImpulseFormInput<WidgetThresholdsStep>>): WidgetThresholdsStep {
		return ImpulseFormShape({
			color: ImpulseFormUnit(initial?.color ?? DEFAULT_STEP.color, {
				isInputDirty: isStringDirty,
				transform: trim,
			}),
			value: ImpulseFormUnit(initial?.value ?? DEFAULT_STEP.value, {
				isInputDirty: isStringDirty,
				schema: z.coerce.number(),
			}),
		});
	},
} as const;

export { WidgetThresholdsStep, WidgetThresholdsMode };
export type { WidgetThresholdModeVariant, WidgetThresholdsColor, WidgetThresholdsValue };
